import { useSyncExternalStore } from 'react'
import {
  NATIVE_BIOMETRIC_SOFT_LOCK_TTL_MS,
  WEB_MFA_TTL_MS,
  isUnlockClockExpired,
  isWebMfaClockExpired,
} from '../features/security/session-security-policy'

/**
 * Soft-lock store: locked flag + per-device clocks.
 *
 * - Native: `lastBiometricUnlockAt` (12h)
 * - Web: `lastWebMfaAt` (7×24h) — separate clock; same soft-lock UX gating
 *
 * Clock durability: WebView localStorage is the synchronous store, but on
 * native it can be evicted (storage pressure, cache clear) or lose a
 * lazily-flushed write to a force-kill — the same failure mode that moved the
 * Cognito tokens to Capacitor Preferences (see amplify-native-storage.ts).
 * On native every clock write goes through to Preferences, and
 * {@link whenSoftLockClocksHydrated} merges the durable copy back before the
 * cold-start lock decision runs.
 *
 * Cognito tokens are never touched here.
 */

const BACKGROUND_KEY = 'ascend-app-lock-backgrounded-at'
const UNLOCK_AT_KEY = 'ascend-last-biometric-unlock-at'
const WEB_MFA_AT_KEY = 'ascend-last-web-mfa-at'

/** @deprecated Prefer NATIVE_BIOMETRIC_SOFT_LOCK_TTL_MS — kept for import stability. */
export const APP_LOCK_IDLE_THRESHOLD_MS = NATIVE_BIOMETRIC_SOFT_LOCK_TTL_MS

export const SOFT_LOCK_TTL_MS = NATIVE_BIOMETRIC_SOFT_LOCK_TTL_MS

let locked = false
const listeners = new Set<() => void>()

function emit(): void {
  for (const listener of listeners) listener()
}

function readNumber(key: string): number | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return null
    const ts = Number(raw)
    return Number.isFinite(ts) ? ts : null
  } catch {
    return null
  }
}

function writeNumber(key: string, ts: number | null): void {
  if (typeof window === 'undefined') return
  try {
    if (ts == null) localStorage.removeItem(key)
    else localStorage.setItem(key, String(ts))
  } catch {
    /* best-effort */
  }
  writeDurable(key, ts)
}

/**
 * Native check via the injected Capacitor global rather than utils/platform —
 * this store also runs in the node-env (shared) test project where the
 * @capacitor/core import chain (config/env → import.meta) cannot load.
 */
function isNativeShell(): boolean {
  if (typeof window === 'undefined') return false
  const cap = (window as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor
  return Boolean(cap?.isNativePlatform?.())
}

/**
 * Write-through to Capacitor Preferences on native (fire-and-forget — the
 * synchronous localStorage copy already reflects the change for this session;
 * Preferences is what survives a force-kill).
 *
 * The plugin proxy is only ever used via direct method calls — never resolved
 * out of a promise — because Capacitor proxies fabricate a `then` method that
 * throws (`"Preferences.then()" is not implemented`).
 */
function writeDurable(key: string, ts: number | null): void {
  if (!isNativeShell()) return
  void (async () => {
    try {
      const { Preferences } = await import('@capacitor/preferences')
      if (ts == null) await Preferences.remove({ key })
      else await Preferences.set({ key, value: String(ts) })
    } catch {
      /* best-effort — localStorage still holds the clock for this session */
    }
  })()
}

async function readDurable(key: string): Promise<number | null> {
  try {
    const { Preferences } = await import('@capacitor/preferences')
    const { value } = await Preferences.get({ key })
    if (!value) return null
    const ts = Number(value)
    return Number.isFinite(ts) ? ts : null
  } catch {
    return null
  }
}

/** Hydration must never stall the lock decision — fall back to localStorage. */
const HYDRATE_TIMEOUT_MS = 3000

let hydrationPromise: Promise<void> | null = null

/**
 * Merge the durable (Preferences) clock copies back into localStorage on
 * native. Resolves once both clocks are reconciled (or the read timed out —
 * the decision then falls back to localStorage, which fails closed: a missing
 * clock means overdue). The newest valid timestamp wins; future-dated values
 * are still rejected at evaluation time by isUnlockClockExpired, so a
 * tampered store cannot extend the window.
 */
export function whenSoftLockClocksHydrated(): Promise<void> {
  if (!isNativeShell()) return Promise.resolve()
  if (!hydrationPromise) {
    const hydrate = (async () => {
      for (const key of [UNLOCK_AT_KEY, WEB_MFA_AT_KEY]) {
        const durable = await readDurable(key)
        const local = readNumber(key)
        const merged = Math.max(durable ?? 0, local ?? 0) || null
        if (merged != null && merged !== local) {
          try {
            localStorage.setItem(key, String(merged))
          } catch {
            /* best-effort */
          }
        }
        if (merged != null && merged !== durable) writeDurable(key, merged)
      }
      emit()
    })()
    hydrationPromise = Promise.race([
      hydrate,
      new Promise<void>((resolve) => setTimeout(resolve, HYDRATE_TIMEOUT_MS)),
    ]).catch(() => {})
  }
  return hydrationPromise
}

/** Test-only: reset the memoized hydration so each test hydrates fresh. */
export function __resetSoftLockHydrationForTests(): void {
  hydrationPromise = null
}

/** Record that the app just left the foreground (backgrounded/hidden). */
export function recordAppBackgrounded(now: number = Date.now()): void {
  writeNumber(BACKGROUND_KEY, now)
}

/**
 * Clear the persisted backgrounded timestamp on foreground. Soft-lock
 * overdue is decided from `lastBiometricUnlockAt`, not background idle —
 * this still clears stale background markers so relaunches don't double-count.
 */
export function clearBackgroundedMarker(): void {
  writeNumber(BACKGROUND_KEY, null)
}

/** @deprecated Use evaluateSoftLockOnForeground — kept for older call sites. */
export function checkIdleThresholdOnForeground(
  thresholdMs: number = SOFT_LOCK_TTL_MS,
  now: number = Date.now(),
): boolean {
  clearBackgroundedMarker()
  return isSoftLockOverdue(thresholdMs, now)
}

export function getLastBiometricUnlockAt(): number | null {
  return readNumber(UNLOCK_AT_KEY)
}

/** Persist a successful biometric unlock and clear the locked flag. */
export function recordBiometricUnlock(now: number = Date.now()): void {
  writeNumber(UNLOCK_AT_KEY, now)
  setAppLocked(false)
}

/** Clear the per-device unlock clock (sign-out / password change — 20A). */
export function clearBiometricUnlockClock(): void {
  writeNumber(UNLOCK_AT_KEY, null)
  writeNumber(BACKGROUND_KEY, null)
  setAppLocked(false)
}

export function getLastWebMfaAt(): number | null {
  return readNumber(WEB_MFA_AT_KEY)
}

/** Persist a successful web weekly MFA verify and clear the locked flag. */
export function recordWebMfaUnlock(now: number = Date.now()): void {
  writeNumber(WEB_MFA_AT_KEY, now)
  setAppLocked(false)
}

/** Clear the per-browser web MFA clock (sign-out / password change — 20A). */
export function clearWebMfaClock(): void {
  writeNumber(WEB_MFA_AT_KEY, null)
}

export function isWebMfaOverdue(
  ttlMs: number = WEB_MFA_TTL_MS,
  now: number = Date.now(),
): boolean {
  return isWebMfaClockExpired(getLastWebMfaAt(), ttlMs, now)
}

export function isSoftLockOverdue(
  ttlMs: number = SOFT_LOCK_TTL_MS,
  now: number = Date.now(),
): boolean {
  return isUnlockClockExpired(getLastBiometricUnlockAt(), ttlMs, now)
}

/**
 * Re-evaluate soft-lock when returning to foreground or on cold start.
 * Returns whether the UI should lock.
 */
export function evaluateSoftLockOnForeground(
  ttlMs: number = SOFT_LOCK_TTL_MS,
  now: number = Date.now(),
): boolean {
  clearBackgroundedMarker()
  return isSoftLockOverdue(ttlMs, now)
}

export function setAppLocked(next: boolean): void {
  if (locked === next) return
  locked = next
  emit()
}

export function isAppLocked(): boolean {
  return locked
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

function getSnapshot(): boolean {
  return locked
}

export function useIsAppLocked(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
}
